import { useMemo } from 'react';

import { AssetPreview } from '@/components/asset-preview';
import { Blocker } from '@/components/blocker';
import { CopySuccessToast } from '@/components/copy-success-toast';
import { DesignerInfoBanner } from '@/components/designer-info-banner';
import { PortfolioBottomNavigation } from '@/components/portfolio-bottom-navigation';
import { ScrollToTopButton } from '@/components/scroll-to-top-button';
import { use_asset_neighbor_preloader } from '@/hook/use_asset_neighbor_preloader';
import { use_app_assets } from '@/hook/use_app_assets';
import { use_current_asset_sequence_number } from '@/hook/use_current_asset_sequence_number';
import {
  get_current_asset_page_number,
  get_total_asset_page_count,
} from '@/lib/asset-pagination';
import { get_should_use_original_image_mode } from '@/lib/image-rendering-mode';
import { create_asset_index_by_sequence_number } from '@/lib/portfolio-navigation';

import './index.css';

const APP_SHELL_CLASS_NAME = 'min-h-screen w-screen bg-[#ECE0B9] md:bg-[#F9F9F9]';
const PORTFOLIO_ASSET_LIST_CLASS_NAME = 'md:pb-[90px] md:pt-[90px]';

export function App() {
  const {
    asset_resources,
    handle_blocker_drag_event,
    handle_refresh_menu_select,
  } = use_app_assets();
  const current_asset_sequence_number = use_current_asset_sequence_number(
    asset_resources,
  );
  const should_use_original_image_mode = useMemo(
    get_should_use_original_image_mode,
    [],
  );
  const asset_index_by_sequence_number = useMemo(
    () =>
      create_asset_index_by_sequence_number(
        asset_resources.map((asset_resource, asset_index) => [
          asset_resource.asset_sequence_number,
          asset_index,
        ]),
      ),
    [asset_resources],
  );
  const handle_asset_view_settle = use_asset_neighbor_preloader(asset_resources);
  const current_asset_page_number = get_current_asset_page_number({
    asset_index_by_sequence_number,
    current_asset_sequence_number,
  });
  const total_asset_page_count = get_total_asset_page_count(asset_resources);

  return (
    <div className={APP_SHELL_CLASS_NAME}>
      <Blocker
        on_blocker_drag_event={handle_blocker_drag_event}
        on_refresh_menu_select={handle_refresh_menu_select}
      />
      <CopySuccessToast />
      <DesignerInfoBanner />
      <ScrollToTopButton
        current_asset_page_number={current_asset_page_number}
        on_refresh_menu_select={handle_refresh_menu_select}
        total_asset_page_count={total_asset_page_count}
      />
      <PortfolioBottomNavigation
        asset_index_by_sequence_number={asset_index_by_sequence_number}
        current_asset_sequence_number={current_asset_sequence_number}
      />
      <main className={PORTFOLIO_ASSET_LIST_CLASS_NAME}>
        {asset_resources.map((asset_resource, asset_index) => (
          <AssetPreview
            key={asset_resource.file_name}
            asset_index_by_sequence_number={asset_index_by_sequence_number}
            asset_index={asset_index}
            asset_resource={asset_resource}
            on_asset_view_settle={handle_asset_view_settle}
            should_use_original_image_mode={should_use_original_image_mode}
          />
        ))}
      </main>
    </div>
  );
}

export default App;
